/**
 * Keeps session replay in step with the four things that can turn it on
 * or off while the app is running: signing in, signing out, flipping the
 * Settings toggle, and the feature flag payload arriving or changing.
 *
 * Mounted once, from `App`. A page reload is the case that needs it most:
 * the auth store rehydrates the user from storage without going through
 * `analytics.identify()`, so hooking the start onto login alone would
 * leave every returning user unrecorded.
 */

import { useEffect, useSyncExternalStore } from "react";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { onFlagsChanged } from "@/lib/feature-flags";
import {
	isReplayOptedOut,
	subscribeToReplayOptOut,
	syncSessionReplay,
} from "@/lib/session-replay";

/** The current opt-out, re-rendering the caller when the toggle moves. */
export function useReplayOptedOut(): boolean {
	return useSyncExternalStore(
		subscribeToReplayOptOut,
		isReplayOptedOut,
		() => false,
	);
}

export function useSessionReplay(): void {
	const authenticated = useAuthStore((state) => Boolean(state.user));
	const optedOut = useReplayOptedOut();

	useEffect(() => {
		syncSessionReplay({ authenticated, optedOut });

		// Flags can arrive after this effect runs, and can change again
		// later — re-evaluating on the payload is what lets the dashboard
		// switch stop an in-progress recording, on posthog-js's ~5-minute
		// poll rather than instantly.
		return onFlagsChanged(() => syncSessionReplay({ authenticated, optedOut }));
	}, [authenticated, optedOut]);
}
